import { useMemo } from 'react';
import { roundToTwoDecimals } from '../../../../../helpers/Units';
import { useRetrieveActiveProject } from '../../../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveActivePlan } from '../plan-details/useRetrieveActivePlan';
import { useRetrieveActivePlanDrivers } from '../plan-details/useRetrieveActivePlanDrivers';

export interface UseRetrieveAnalyticsPlanCapacitiesResponse {
  totalWeight: number;
  totalVolume: number;
  capacityWeight: number;
  capacityVolume: number;
  weightRate: number;
  volumeRate: number;
  vehiclesWithoutWeight: number;
  vehiclesWithoutVolume: number;
}

export const useRetrieveAnalyticsPlanCapacities = (): UseRetrieveAnalyticsPlanCapacitiesResponse => {
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: planDrivers } = useRetrieveActivePlanDrivers();
  const { data: activePlan } = useRetrieveActivePlan();

  const {
    totalWeight,
    totalVolume,
    capacityWeight,
    capacityVolume,
    weightRate,
    volumeRate,
    vehiclesWithoutWeight,
    vehiclesWithoutVolume,
  } = useMemo<UseRetrieveAnalyticsPlanCapacitiesResponse>(() => {
    if (!activePlan || !activeProject || !planDrivers) {
      return {
        totalWeight: 0,
        totalVolume: 0,
        capacityWeight: 0,
        capacityVolume: 0,
        weightRate: 0,
        volumeRate: 0,
        vehiclesWithoutWeight: 0,
        vehiclesWithoutVolume: 0,
      };
    }

    const totalWeight = planDrivers.reduce((prev, driver) => prev + driver.getAssignedWeight(), 0);
    const totalVolume = planDrivers.reduce((prev, driver) => prev + driver.getAssignedVolume(), 0);
    const capacityWeight = planDrivers.reduce((prev, driver) => prev + (driver.max_weight ?? 0), 0);
    const capacityVolume = planDrivers.reduce((prev, driver) => prev + (driver.max_volume ?? 0), 0);

    return {
      totalWeight,
      totalVolume,
      capacityWeight,
      capacityVolume,
      weightRate: roundToTwoDecimals(capacityWeight > 0 ? (totalWeight / capacityWeight) * 100 : 0),
      volumeRate: roundToTwoDecimals(capacityVolume > 0 ? (totalVolume / capacityVolume) * 100 : 0),

      vehiclesWithoutWeight: planDrivers.filter((driver) => !driver.max_weight).length,
      vehiclesWithoutVolume: planDrivers.filter((driver) => !driver.max_volume).length,
    };
  }, [activePlan, planDrivers, activeProject]);

  return {
    totalWeight,
    totalVolume,
    capacityWeight,
    capacityVolume,
    weightRate,
    volumeRate,
    vehiclesWithoutWeight,
    vehiclesWithoutVolume,
  };
};
